import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { ChevronRight } from "lucide-react";

/**
 * §MobileQA: the decision-card bullets use the design's "fill icon/attention-filled" asset
 * (node I12107:45905) — a SOLID disc + exclamation — not lucide's outline circle-i. The same
 * path ships as AttentionFilled in step-status-icon.tsx but is private to that file, so the
 * 18×18 path is repeated here rather than reaching across module boundaries. The design tints
 * it Layer/Tertiary Dark (#6D8395), the slate the list glyphs use everywhere in this frame.
 */
const AttentionFilled = ({ size, fill = "#6D8395" }: { size: number; fill?: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M9 0.75C13.5563 0.75 17.25 4.44365 17.25 9C17.25 13.5563 13.5563 17.25 9 17.25C4.44365 17.25 0.75 13.5563 0.75 9C0.75 4.44365 4.44365 0.75 9 0.75ZM9 12C8.58579 12 8.25 12.3358 8.25 12.75V13.125C8.25 13.5392 8.58579 13.875 9 13.875C9.41421 13.875 9.75 13.5392 9.75 13.125V12.75C9.75 12.3358 9.41421 12 9 12ZM9 4.5C8.58579 4.5 8.25 4.83579 8.25 5.25V10.125C8.25 10.5392 8.58579 10.875 9 10.875C9.41421 10.875 9.75 10.5392 9.75 10.125V5.25C9.75 4.83579 9.41421 4.5 9 4.5Z"
      fill={fill}
    />
  </svg>
);

/**
 * §W3.2 Credit-feedback decision cards. Replaces the plain "Resolution path" radio on the
 * Credit Feedback step with the Figma three-option card stack. Copy is taken VERBATIM from the
 * design — not invented — and keyed by the engine's radio option VALUE, so the cards follow whatever
 * paths the credit reviewer actually offered (hiddenOptionValues already filters them). The amount on
 * the "Accept approved amount" card is the DBR-eligible max the accept-approved-amount function applies.
 *
 * §Modals (25-Jul canvas 11508:321522, verified against frames 12107:64726 / 60805 / 45495):
 * a card's CTA no longer confirms in one click — it opens that path's confirmation modal
 * ("Submit extra documents" / "Accept loan?" / "Request guarantor"), whose copy spells out the
 * consequence, and the Accept there fires confirm-path. Card order, eyebrow labels, the
 * "Recommended" chip on the document-upgrade card and the filled-vs-tonal CTAs are all as the
 * 25-Jul frames render them (the amount-first variant in the "Proceed with amount" section is the
 * same stack under a different credit offer, not a re-order of this one).
 */
type CardMeta = {
  eyebrow: string;
  title: string;
  desc: string;
  bullets?: string[];
  footnote?: string;
  cta: string;
  /** the recommended card carries the chip, the dashed accent border and the filled CTA */
  recommended?: boolean;
  showAmount?: boolean;
  showDbrLink?: boolean;
  /** this path's confirmation modal (copy verbatim from the design) */
  confirm: {
    title: string;
    message: string;
    footnote?: string;
    confirmLabel: string;
    /** design shows the consequence in a negative alert band rather than plain body copy */
    alert?: boolean;
    /** design shows the loan amount in a positive-tinted panel under the message */
    amountPanel?: boolean;
  };
};

/** keyed by the engine's radio option value (see the deal-flow "Path Selection.Resolution path" field) */
const CARD_META: Record<string, CardMeta> = {
  "Extra documents": {
    eyebrow: "Option A • Document upgrade",
    title: "Submit additional documents",
    desc: "Customer provides extra income proof & updated payment plan to qualify for a higher amount.",
    bullets: ["Upload additional income documents", "Update payment plan"],
    footnote: "* All requirements must be fulfilled & re-reviewed by credit",
    cta: "Update details",
    recommended: true,
    confirm: {
      title: "Submit extra documents",
      message:
        "Request more documents & update payment plan? Please note that this amount will vary depending on documents provided.",
      footnote: "* You can change this later if client needs a smaller loan",
      confirmLabel: "Accept",
    },
  },
  "Accept approved amount": {
    eyebrow: "Option B • Quickest path",
    title: "Accept approved amount",
    desc: "Proceed with the approved amount. No additional actions required from the customer.",
    cta: "Accept & proceed",
    showAmount: true,
    showDbrLink: true,
    confirm: {
      title: "Accept loan?",
      // {amount} is substituted with the DBR-eligible max the function will apply
      message:
        "Accept loan amount of {amount} & proceed in the process? Please note that this amount is less than originally requested amount.",
      footnote: "* You can change this later if client needs bigger loan",
      confirmLabel: "Accept",
      amountPanel: true,
    },
  },
  Guarantor: {
    eyebrow: "Option C • Guarantor path",
    title: "Add a guarantor",
    desc: "A secondary guarantor covers the gap. Their iScore & KYC will be requested.",
    bullets: ["Guarantor iScore", "Guarantor KYC", "Guarantor credit documents"],
    footnote: "* Guarantor must pass credit checks before approval is finalized.",
    cta: "Add guarantor",
    confirm: {
      title: "Request guarantor",
      message:
        "This will send a request to the guarantor for iScore, KYC & credit documents will be requested",
      confirmLabel: "Request guarantor",
      alert: true,
    },
  },
};

/** design render order (A → B → C), independent of the engine's option order */
const ORDER = ["Extra documents", "Accept approved amount", "Guarantor"];

/** the design's confirmation modal for a chosen path, with the live amount substituted in */
export const creditPathConfirm = (
  optionValue: string,
  approvedAmount?: number
): CardMeta["confirm"] | null => {
  const meta = CARD_META[optionValue];
  if (!meta) return null;
  return {
    ...meta.confirm,
    message: meta.confirm.message.replace(
      "{amount}",
      approvedAmount != null ? formatEgp(approvedAmount) : "the approved amount"
    ),
  };
};

export const isCreditFeedbackOptions = (options: string[]): boolean =>
  options.length > 0 && options.every((o) => o in CARD_META);

const formatEgp = (n: number): string => `${Math.round(n).toLocaleString("en-US")} EGP`;

const CreditFeedbackCards = ({
  options,
  approvedAmount,
  busy,
  onSelect,
  onViewDbr,
}: {
  /** already filtered to the visible (offered) paths */
  options: string[];
  approvedAmount?: number;
  busy: boolean;
  onSelect: (optionValue: string) => void;
  /** opens the "Preview DBR" modal behind the amount card's link */
  onViewDbr: () => void;
}) => {
  const theme = useTheme();
  const c = theme.nawy.colors.semantic;
  const visible = ORDER.filter((v) => options.includes(v));

  return (
    // §MobileQA: the design wraps the decision stack in a "Feedback" container — Layer/Negative
    // Light ground, radius xl, 16px padding and gap — titled "Credit team decision". The cards
    // were rendering bare, so the stack read as three unrelated cards rather than one verdict.
    // The design also shows a sentence explaining WHY under the title; that text is the credit
    // team's own reasoning and belongs to the engine, so it is NOT hard-coded here.
    <Box
      sx={{
        p: 2,
        borderRadius: theme.nawy.radius.xl,
        backgroundColor: c.layer.extension.negativeLight,
      }}
    >
      <Typography sx={{fontSize: 14, fontWeight: 600, color: c.text.primary, mb: 1}}>
        Credit team decision
      </Typography>
      {/* design: the Three-Option Decision Cards Stack is gap xs (8px), not 12px */}
      <Stack spacing={1}>
      {visible.map((value) => {
        const m = CARD_META[value];
        const accent = m.recommended;
        const filled = m.recommended || visible.length === 1;
        return (
          <Box
            key={value}
            sx={{
              p: 2,
              borderRadius: theme.nawy.radius.xl,
              // design: every card sits on white — the recommended one is marked by a DASHED
              // negative-tone border + the chip, not by a tinted ground. §MobileQA: the dash is
              // Border/Negative #DA3E33 at 0.5px (was #C72D22 at 1px), and the other cards carry
              // NO border at all — a grey outline boxed them against the tinted feedback ground.
              backgroundColor: c.layer.primary,
              border: accent ? `0.5px dashed ${c.layer.extension.negative}` : "none",
            }}
          >
            {/* §MobileQA: the chip is a flex SIBLING of the flex-1 title block, not an absolutely
                positioned overlay — out of flow it sat 4px outside the card's own 16px inset and a
                wrapping title ran underneath it. */}
            <Stack direction="row" spacing={0.5} alignItems="flex-start">
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 10, lineHeight: "16px", color: c.text.secondary }}>
                  {m.eyebrow}
                </Typography>

                {/* design: title is Body/md - Semibold 14/600/20, and the amount is a WRAPPING
                    sibling (4px gap) in Text/Positive — inline it broke mid-phrase at 390px. */}
                <Box
                  sx={{
                    mt: 0.25,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                    fontSize: 14,
                    lineHeight: "20px",
                    fontWeight: 600,
                  }}
                >
                  <Typography component="span" sx={{ fontSize: "inherit", lineHeight: "inherit", fontWeight: "inherit", color: c.text.primary }}>
                    {m.title}
                  </Typography>
                  {m.showAmount && approvedAmount != null && (
                    <Typography component="span" sx={{ fontSize: "inherit", lineHeight: "inherit", fontWeight: "inherit", color: c.text.extension.positive }}>
                      {formatEgp(approvedAmount)}
                    </Typography>
                  )}
                </Box>
              </Box>

              {m.recommended && (
                <Box
                  sx={{
                    px: "4px",
                    py: 0,
                    flexShrink: 0,
                    borderRadius: theme.nawy.radius.md,
                    // design: the Indicator green ramp (same values the step-card status pills
                    // ship) — the positiveLight/positive pair was a much paler mint.
                    backgroundColor: "#CFE8E2",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10,
                      lineHeight: "16px",
                      fontWeight: 400,
                      whiteSpace: "nowrap",
                      color: "#326054",
                    }}
                  >
                    Recommended
                  </Typography>
                </Box>
              )}
            </Stack>

            {/* design: description and bullet labels are Body/sm - Regular 12/400/18 — at 14/20
                they pushed every card ~40% taller than the frame. */}
            <Typography
              sx={{ mt: 0.5, fontSize: 12, lineHeight: "18px", color: c.text.secondary }}
            >
              {m.desc}
            </Typography>

            {m.bullets && (
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                {m.bullets.map((b) => (
                  <Stack key={b} direction="row" spacing={0.5} alignItems="center">
                    <AttentionFilled size={16} fill={c.layer.tertiaryDark} />
                    <Typography sx={{ fontSize: 12, lineHeight: "18px", color: c.text.primary }}>
                      {b}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            )}

            {m.showDbrLink && (
              // design: a quiet inline link — Body/sm 12/400/18 in Text/Accent, 8px before a 14px
              // chevron, sitting 4px under the description (it read as a bolded button at 14/600).
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                onClick={onViewDbr}
                sx={{
                  mt: 0.5,
                  color: c.text.accent,
                  cursor: "pointer",
                  width: "fit-content",
                }}
              >
                <Typography sx={{ fontSize: 12, lineHeight: "18px", fontWeight: 400 }}>
                  View DBR
                </Typography>
                <ChevronRight size={14} />
              </Stack>
            )}

            {m.footnote && (
              <Typography
                sx={{
                  mt: 1.5,
                  // design: Body/xxs 10/400/16, upright — the footnote is never italicised
                  fontSize: 10,
                  lineHeight: "16px",
                  color: c.text.tertiary,
                }}
              >
                {m.footnote}
              </Typography>
            )}

            {/* §MobileQA: the tonal CTA only expresses hierarchy when there is something to rank
                against — the guarantor-only frame shows its lone "Add guarantor" CTA FILLED. A
                single visible path therefore gets the filled treatment (which is also what
                §BlueButtons asks for); only the label/action stays untouched. */}
            <Button
              variant={filled ? "contained" : "text"}
              disableElevation
              disabled={busy}
              onClick={() => onSelect(value)}
              sx={{
                mt: 2,
                // design: every CTA in the decision stack is the 32px button
                height: 32,
                px: 2,
                borderRadius: theme.nawy.radius.lg,
                fontSize: 14,
                fontWeight: 600,
                lineHeight: "20px",
                textTransform: "none",
                boxShadow: "none",
                ...(filled
                  ? {
                      // §MobileQA: was layer.accentDark = #002741 (near-black navy). The design's
                      // recommended CTA is Layer/Accent #015C9A, which is also what §BlueButtons asks for.
                      backgroundColor: c.layer.accent,
                      color: c.text.onColor,
                      "&:hover": { boxShadow: "none", backgroundColor: c.layer.accentDark },
                    }
                  : {
                      backgroundColor: c.layer.accentLight,
                      color: c.text.accent,
                      "&:hover": { backgroundColor: c.layer.accentLight },
                    }),
              }}
            >
              {m.cta}
            </Button>
          </Box>
        );
      })}
      </Stack>
    </Box>
  );
};

export default CreditFeedbackCards;
